import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUiTranslation } from "@tubo/ui/i18n";
import { ArrowLeft, Calculator, CircleCheck, Plus, Save, Trash2, Upload } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@tubo/ui/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@tubo/ui/components/ui/sheet";
import { Textarea } from "@tubo/ui/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import {
  useQuantityKindDetail,
  useUpdateUnits,
  useConvertPreview,
  useImportUnits,
  usePublishQuantityKind,
  getQuantityKindDetailQueryKey,
  getQuantityKindListQueryKey,
} from "@tubo/api-models/hooks/quantity-kind-api.hooks";
import type { UnitDefinitionVO, UnitImportResp, UnitImportRowVO } from "@tubo/api-models/hooks/quantity-kind-api.schemas";
import { ResourcePublishStatusEnum } from "@tubo/api-models/business/platform/platform.enums";
import { ConversionRuleFields, ConversionTypeSelect } from "@client/components/semantic-platform/quantities/unit-conversion-fields";
import { QuantityBasicInfoPanel } from "@client/components/semantic-platform/quantities/quantity-basic-info-panel";
import { publishStatusVariant } from "@client/components/semantic-platform/publish-status";

const PUBLISH_STATUS_KEY: Record<string, "draft" | "pendingReview" | "published" | "deprecated"> = {
  [ResourcePublishStatusEnum.Draft]: "draft",
  [ResourcePublishStatusEnum.PendingReview]: "pendingReview",
  [ResourcePublishStatusEnum.Published]: "published",
  [ResourcePublishStatusEnum.Deprecated]: "deprecated",
};
type SemanticDetailsTranslate = ReturnType<typeof useUiTranslation>["t"];

export const Route = createFileRoute("/_app/semantic-platform/quantities/$id")({
  component: QuantityDetailPage,
});

function QuantityDetailPage() {
  const { t } = useUiTranslation("semanticPlatformDetails");
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { searchStr } = useLocation();
  const listSearch = new URLSearchParams(searchStr).get("ns");
  const queryClient = useQueryClient();
  const { data: quantity } = useQuantityKindDetail(id, { query: {} });

  const [units, setUnits] = useState<UnitDefinitionVO[]>([]);
  const [standardUnit, setStandardUnit] = useState("");
  const [calcValue, setCalcValue] = useState("100");
  const [calcUnit, setCalcUnit] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [unitDeleteIdx, setUnitDeleteIdx] = useState<number | null>(null);

  useEffect(() => {
    if (quantity?.units) {
      setUnits(quantity.units);
      setStandardUnit(quantity.standardUnit);
      setCalcUnit((prev) => prev || quantity.standardUnit);
    }
  }, [quantity]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getQuantityKindDetailQueryKey(id) });
    void queryClient.invalidateQueries({ queryKey: getQuantityKindListQueryKey() });
  };

  const { mutate: saveUnits, isPending: savingUnits } = useUpdateUnits({
    mutation: {
      onSuccess: () => { invalidate(); toast.success(t("quantityDetail.saved")); },
      onError: () => toast.error(t("quantityDetail.saveFailed")),
    },
  });

  const publishMutation = usePublishQuantityKind({
    mutation: {
      onSuccess: (detail) => {
        invalidate();
        toast.success(t("quantityDetail.published", { name: detail?.name ?? t("quantityDetail.title") }));
      },
      onError: () => toast.error(t("quantityDetail.publishFailed")),
    },
  });

  const [previewResult, setPreviewResult] = useState<{ standardUnit: string; converted: number; formula: string } | null>(null);
  const calcMutation = useConvertPreview({
    mutation: {
      onSuccess: (res) => setPreviewResult(res),
      onError: () => toast.error(t("quantityDetail.conversionFailed")),
    },
  });

  const dirty = quantity
    ? standardUnit !== quantity.standardUnit || JSON.stringify(units) !== JSON.stringify(quantity.units ?? [])
    : false;

  const addUnit = () => setUnits((prev) => [...prev, { symbol: "", name: "" }]);
  const patchUnit = (idx: number, patch: Partial<UnitDefinitionVO>) =>
    setUnits((prev) => prev.map((u, i) => (i === idx ? { ...u, ...patch } : u)));
  const removeUnit = (idx: number) => setUnits((prev) => prev.filter((_, i) => i !== idx));

  // 头部注入域级 SecondaryNav(标题区:返回/编码/名称/标准单位;操作区:换算预览/导入单位/保存),页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" aria-label={t("quantityDetail.backToList")} onClick={() => navigate({ to: "/semantic-platform/quantities", search: listSearch ? { ns: listSearch } : {} })}>
          <ArrowLeft className="size-4" />
        </Button>
        {quantity ? (
          <>
            <span className="font-mono text-sm text-muted-foreground">{quantity.code}</span>
            <span className="text-sm font-semibold">{quantity.name}</span>
            <Badge variant="outline">{t("quantityDetail.standardUnit", { unit: quantity.standardUnit })}</Badge>
            <Badge variant={publishStatusVariant(quantity.status)}>{PUBLISH_STATUS_KEY[quantity.status] ? t(`quantityDetail.status.${PUBLISH_STATUS_KEY[quantity.status]}`) : quantity.status}</Badge>
          </>
        ) : (
          <span className="text-sm font-semibold">{t("quantityDetail.title")}</span>
        )}
      </div>
    ),
    [quantity, navigate, listSearch, t],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () =>
      quantity ? (
        <div className="flex items-center gap-2">
          {quantity.status !== "published" && (
            <Button variant="outline" size="sm" disabled={publishMutation.isPending} onClick={() => publishMutation.mutate({ id })}>
              <CircleCheck className="mr-1 size-4" /> {t("quantityDetail.publish")}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
            <Calculator className="mr-1 size-4" /> {t("quantityDetail.conversionPreview")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="mr-1 size-4" /> {t("quantityDetail.importUnits")}
          </Button>
          <Button size="sm" disabled={!dirty || savingUnits} onClick={() => saveUnits({ id, data: { standardUnit, units } })}>
            <Save className="mr-1 size-4" /> {t("quantityDetail.saveUnits")}
          </Button>
        </div>
      ) : null,
    [quantity, dirty, savingUnits, saveUnits, id, standardUnit, units, publishMutation, t],
  );
  useSecondaryNavActions(headerActions);

  if (!quantity) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">{t("quantityDetail.loading")}</div>;
  }

  const unitDeleteTarget = unitDeleteIdx !== null ? units[unitDeleteIdx] : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto p-4 xl:p-6">
        <div className="space-y-4">
          <QuantityBasicInfoPanel quantity={quantity} onSaved={invalidate} />

          <section className="rounded-lg border bg-surface/50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              {t("quantityDetail.unitsTitle")}
            </h2>
            <Button variant="outline" size="sm" onClick={addUnit}>
              <Plus className="mr-1 size-4" /> {t("quantityDetail.addUnit")}
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24 text-left">{t("quantityDetail.fields.symbol")}</TableHead>
                <TableHead className="w-32 text-left">{t("quantityDetail.fields.name")}</TableHead>
                <TableHead className="w-16 text-left">{t("quantityDetail.fields.standard")}</TableHead>
                <TableHead className="w-28 text-left">{t("quantityDetail.fields.conversionType")}</TableHead>
                <TableHead className="text-left">{t("quantityDetail.fields.conversionRule")}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map((u, idx) => (
                <UnitRow
                  key={idx}
                  unit={u}
                  t={t}
                  isStandard={standardUnit === u.symbol}
                  onPatch={(patch) => patchUnit(idx, patch)}
                  onRemove={() => setUnitDeleteIdx(idx)}
                  onMakeStandard={() => {
                    setStandardUnit(u.symbol);
                    setUnits((prev) => prev.map((x, i) => ({ ...x, isStandard: i === idx })));
                  }}
                />
              ))}
            </TableBody>
          </Table>
          </section>
        </div>
      </div>

      <AlertDialog open={unitDeleteIdx !== null} onOpenChange={(open) => !open && setUnitDeleteIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("quantityDetail.removeTitle", { symbol: unitDeleteTarget?.symbol ?? "", name: unitDeleteTarget?.name ?? "" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("quantityDetail.removeDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("quantityDetail.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => unitDeleteIdx !== null && removeUnit(unitDeleteIdx)}
            >
              {t("quantityDetail.confirmRemove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="w-[min(420px,100vw)] gap-0 overflow-y-auto sm:max-w-none">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Calculator className="size-4" />
              {t("quantityDetail.previewTitle")}
            </SheetTitle>
            <SheetDescription>
              {t("quantityDetail.previewDescription")}
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 px-4">
            <div className="grid gap-1.5">
              <Label htmlFor="quantity-preview-value">{t("quantityDetail.inputValue")}</Label>
              <Input
                id="quantity-preview-value"
                type="number"
                value={calcValue}
                onChange={(e) => setCalcValue(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="quantity-preview-unit">{t("quantityDetail.inputUnit")}</Label>
              <Select
                items={units.filter((u) => u.symbol).map((u) => ({ value: u.symbol, label: u.symbol }))}
                value={calcUnit}
                onValueChange={(v) => setCalcUnit(v ?? "")}
              >
                <SelectTrigger id="quantity-preview-unit" className="w-full">
                  <SelectValue placeholder={t("quantityDetail.chooseUnit")} />
                </SelectTrigger>
                <SelectContent>
                  {units.filter((u) => u.symbol).map((u) => (
                    <SelectItem key={u.symbol} value={u.symbol}>{u.symbol}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md bg-muted/40 p-3 text-sm">
              {previewResult ? (
                <>
                  <div className="font-medium">
                    {calcValue} {calcUnit} = <span className="text-primary">{Number(previewResult.converted.toFixed(6))}</span> {previewResult.standardUnit}
                  </div>
                  <div className="mt-1 font-mono text-xs text-muted-foreground">{previewResult.formula}</div>
                </>
              ) : (
                <span className="text-muted-foreground">{t("quantityDetail.calculateHint")}</span>
              )}
            </div>
          </div>
          <SheetFooter>
            <Button
              disabled={!calcUnit || calcValue.trim() === "" || isNaN(Number(calcValue)) || calcMutation.isPending}
              onClick={() => calcMutation.mutate({ params: { id }, data: { value: Number(calcValue), fromUnit: calcUnit } })}
            >
              {t("quantityDetail.calculate")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ImportUnitsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        id={id}
        standardUnit={quantity.standardUnit}
        onImported={() => { invalidate(); setImportOpen(false); }}
      />
    </div>
  );
}

/** 单位行(8D-3②:换算类型切换——linear 因子/偏移;expression 受限表达式;piecewise 分段) */
function UnitRow({
  unit,
  t,
  isStandard,
  onPatch,
  onRemove,
  onMakeStandard,
}: {
  unit: UnitDefinitionVO;
  t: SemanticDetailsTranslate;
  isStandard: boolean;
  onPatch: (patch: Partial<UnitDefinitionVO>) => void;
  onRemove: () => void;
  onMakeStandard: () => void;
}) {
  return (
    <TableRow>
      <TableCell><Input className="h-8" value={unit.symbol} onChange={(e) => onPatch({ symbol: e.target.value })} /></TableCell>
      <TableCell><Input className="h-8" value={unit.name} onChange={(e) => onPatch({ name: e.target.value })} /></TableCell>
      <TableCell>
        <input type="radio" name="standard-unit" checked={isStandard} onChange={onMakeStandard} />
      </TableCell>
      <TableCell>
        <ConversionTypeSelect value={unit.conversionType} onChange={(conversionType) => onPatch({ conversionType })} />
      </TableCell>
      <TableCell>
        {isStandard ? (
          <span className="text-xs text-muted-foreground">{t("quantityDetail.standardIdentity")}</span>
        ) : (
          <ConversionRuleFields unit={unit} onPatch={onPatch} />
        )}
      </TableCell>
      <TableCell>
        <Button variant="ghost" size="icon" className="size-7" disabled={isStandard} onClick={onRemove}>
          <Trash2 className="size-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

/** 外部单位制导入对话框(8D-3③:CSV/UCUM 映射;两段式——预览冲突/影响,确认后执行) */
function ImportUnitsDialog({
  open,
  onOpenChange,
  id,
  standardUnit,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id: string;
  standardUnit: string;
  onImported: () => void;
}) {
  const { t } = useUiTranslation("semanticPlatformDetails");
  const [csv, setCsv] = useState("");
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [preview, setPreview] = useState<UnitImportResp | null>(null);
  const [draft, setDraft] = useState<UnitImportRowVO[]>([]);
  const importModeOptions = [
    { value: "merge", label: t("quantityDetail.importMode.merge") },
    { value: "replace", label: t("quantityDetail.importMode.replace") },
  ];

  const importMutation = useImportUnits({
    mutation: {
      onSuccess: (res) => {
        if (res.preview) {
          setPreview(res);
        } else {
          toast.success(t("quantityDetail.imported", { created: res.addCount, updated: res.updateCount }));
          onImported();
        }
      },
      onError: () => toast.error(t("quantityDetail.importFailed")),
    },
  });

  /** 解析 CSV:symbol,name,factor,offset[,conversionType,expression];注释行/空行跳过 */
  const parseCsv = () => {
    const rows: UnitImportRowVO[] = [];
    for (const line of csv.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith("#")) continue;
      const cols = trimmed.split(",").map((s) => s.trim());
      if (cols.length < 2 || !cols[0] || !cols[1]) continue;
      const row: UnitImportRowVO = { symbol: cols[0], name: cols[1] };
      if (cols[2]) row.convertFactor = Number(cols[2]);
      if (cols[3]) row.convertOffset = Number(cols[3]);
      if (cols[4] === "expression") {
        row.conversionType = "expression";
        if (cols[5]) row.expression = cols[5];
      } else if (cols[4] === "piecewise") {
        row.conversionType = "piecewise";
      }
      rows.push(row);
    }
    if (rows.length === 0) {
      toast.error(t("quantityDetail.noValidRows"));
      return;
    }
    setDraft(rows);
    // 首次预览:不执行
    importMutation.mutate({
      id,
      data: { standardUnit, units: rows, mode, confirm: false },
    });
  };

  const confirmImport = () => {
    importMutation.mutate({
      id,
      data: { standardUnit, units: draft, mode, confirm: true },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setPreview(null); setDraft([]); } }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("quantityDetail.importTitle")}</DialogTitle>
          <DialogDescription>
            {t("quantityDetail.importDescription", { unit: standardUnit })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Textarea
            rows={6}
            value={csv}
            placeholder={t("quantityDetail.csvPlaceholder")}
            onChange={(e) => setCsv(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Select items={importModeOptions} value={mode} onValueChange={(v) => setMode(v === "replace" ? "replace" : "merge")}>
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {importModeOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={parseCsv} disabled={importMutation.isPending}>
              {t("quantityDetail.preview")}
            </Button>
            {preview && (
              <Button size="sm" onClick={confirmImport} disabled={importMutation.isPending}>
                {t("quantityDetail.confirmImport")}
              </Button>
            )}
          </div>
          {preview && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <p>{t("quantityDetail.previewCounts", { added: preview.addCount, updated: preview.updateCount, conflicts: preview.conflictCount, references: preview.modelAttributeCount })}</p>
              {preview.conflicts && preview.conflicts.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs text-destructive">
                  {preview.conflicts.map((c) => (
                    <li key={c.symbol}>· {t("quantityDetail.conflict", { symbol: c.symbol, existing: c.existingName, incoming: c.incomingName })}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>{t("quantityDetail.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
